/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { classNamesFunction, IDialogContentStyleProps, IDialogContentStyles, Stack } from '@fluentui/react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getRandomNumber } from 'src/core/utils/randomValueGenerators';
import { AMLConnectionInfo } from 'src/customText/core/stores/CustomTextProjectStore';
import { AzureMLConnectionStepsNavigator } from 'src/customText/verticals/azureMLConnectionModal/components/AzureMLConnectionStepsNavigator';
import { ConnectionPage } from 'src/customText/verticals/azureMLConnectionModal/components/ConnectionPage';
import { InstructionPage } from 'src/customText/verticals/azureMLConnectionModal/components/InstructionsPage';
import { OutsourcingPage } from 'src/customText/verticals/azureMLConnectionModal/components/OutsourcingPage';
import { OverviewPage } from 'src/customText/verticals/azureMLConnectionModal/components/OverviewPage';
import { ReviewPage } from 'src/customText/verticals/azureMLConnectionModal/components/ReviewPage';
import styled from 'styled-components';

export enum AMLConnectionSteps {
    Overview = 0,
    Connection = 1,
    Outsourcing = 2,
    Instructions = 3,
    Review = 4
}

const messages = defineMessages({
    modalTitle: {
        id: 'customText.azureMLConnectionModal.modalTitle',
        defaultMessage: 'Connect your project to Azure Machine Learning'
    }
});

const modalTheme = getScopedTheme('AzureMLConnectionModal');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const dialogContentStyles = classNamesFunction<IDialogContentStyleProps, IDialogContentStyles>()({
    inner: { paddingTop: '0px', paddingBottom: '0px', borderTop: borderStyle }
});
const ContentWrapper = styled(Stack)<{ isLargeOrGreater: boolean }>(props => ({
    paddingTop: '28px',
    width: props.isLargeOrGreater ? '80%' : '',
    minHeight: '80vh',
    color: modalTheme.defaultTextColor,
    minWidth: 0,
    position: 'relative'
}));
const NavigatorWrapper = styled(Stack.Item)<{ isLargeOrGreater: boolean }>(props => ({
    width: props.isLargeOrGreater ? '200px' : '',
    borderRight: props.isLargeOrGreater ? borderStyle : '',
    padding: `${props.isLargeOrGreater ? '34px 0 0 10px' : '12px 20px'}`
}));

const getInitialAMLConnectionInfo: AMLConnectionInfo = {
    subscription: '',
    storageAccount: '',
    labelingTaskType: '',
    projectLanguage: '',
    containerName: '',
    projectName: '',
    azureMLWorkspace: '',
    outsourcing: false,
    labelingInstructionText: '',
    vendor: '',
    storageResourceGroup: '',
    labelingInstructionUrl: '',
    subscriptionId: '',
    datasetId: '',
    projectId: '',
    workspaceRegion: ''
};

const getProperName = (str: string) => str.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();

export const AzureMLConnectionModal = (props: BasicModalProps<Object>) => {
    const { onDismiss } = props;
    const { localize } = useLocalization();
    const { isLargeOrGreater } = useResponsiveMode();
    const [activeStep, setActiveStep] = React.useState(AMLConnectionSteps.Overview);
    const [amlConnectionInfo, updateAmlConnectionInfo] = useUpdateState<AMLConnectionInfo>(getInitialAMLConnectionInfo);
    const { rootStore, dispatcher } = useGlobalState();
    const { projectKind } = rootStore.currentCustomTextProjectStore;
    const isExtractionProject = projectKind === 'CustomEntityRecognition';
    const initializingAmlWorkspaceProjectTracker = useProgressTracker({ persistOnComplete: true });
    const createAmlProjectTracker = useProgressTracker({
        persistOnComplete: true,
        onComplete: onDismiss
    });

    const onNext = (connectionInfo?: Partial<AMLConnectionInfo>) => {
        if (activeStep === AMLConnectionSteps.Review) {
            const properName = getProperName(amlConnectionInfo.projectName);
            const dataStoreName = `${properName}_datastore_${getRandomNumber()}`;
            const dataAssetsName = `${properName}_dataassets_${getRandomNumber()}`;
            logButtonClick(isExtractionProject ? 'ner' : 'classification', 'ReviewPage', 'AML:CreateAMLProject', 'AzureMachineLearning');
            dispatcher.dispatch('createAmlProject', {
                amlConnectionInfo,
                dataAssetsName,
                dataStoreName,
                tracker: createAmlProjectTracker
            });
        } else {
            if (connectionInfo) updateAmlConnectionInfo(connectionInfo);
            setActiveStep(activeStep + 1);
        }
    };

    const onPrevious = () => {
        setActiveStep(activeStep - 1);
    };

    const handleDismissAmlModal = () => {
        setActiveStep(AMLConnectionSteps.Overview);
        onDismiss();
    };

    const getStepContent = () => {
        switch (activeStep) {
            case AMLConnectionSteps.Overview:
                return <OverviewPage onDismiss={handleDismissAmlModal} onNext={() => onNext(amlConnectionInfo)} />;
            case AMLConnectionSteps.Connection:
                return (
                    <ConnectionPage
                        amlConnectionInfo={amlConnectionInfo}
                        onPrevious={onPrevious}
                        onDismiss={handleDismissAmlModal}
                        onNext={onNext}
                    />
                );
            case AMLConnectionSteps.Outsourcing:
                return (
                    <OutsourcingPage
                        onPrevious={onPrevious}
                        onDismiss={handleDismissAmlModal}
                        onNext={onNext}
                        amlConnectionInfo={amlConnectionInfo}
                    />
                );

            case AMLConnectionSteps.Instructions:
                return (
                    <InstructionPage
                        onPrevious={onPrevious}
                        onDismiss={handleDismissAmlModal}
                        onNext={onNext}
                        amlConnectionInfo={amlConnectionInfo}
                    />
                );
            case AMLConnectionSteps.Review:
                return (
                    <ReviewPage
                        onPrevious={onPrevious}
                        onDismiss={handleDismissAmlModal}
                        onNext={onNext}
                        amlConnectionInfo={amlConnectionInfo}
                        createAmlProjectTracker={createAmlProjectTracker}
                        initializingAmlWorkspaceProjectTracker={initializingAmlWorkspaceProjectTracker}
                    />
                );

            default:
        }
    };

    return (
        <BasicModal
            {...props}
            isClickableOutsideFocusTrap={false}
            minWidth={976}
            modalProps={{ isModeless: true }}
            shouldRenderFooter={false}
            dialogContentProps={{ title: localize(messages.modalTitle), styles: dialogContentStyles }}>
            <Stack horizontal={isLargeOrGreater} verticalFill>
                <NavigatorWrapper isLargeOrGreater={isLargeOrGreater}>
                    <AzureMLConnectionStepsNavigator
                        activeStep={activeStep}
                        horizontal={!isLargeOrGreater}
                        onActiveStepChange={setActiveStep}
                    />
                </NavigatorWrapper>
                <ContentWrapper isLargeOrGreater={isLargeOrGreater} grow verticalFill>
                    {getStepContent()}
                </ContentWrapper>
            </Stack>
        </BasicModal>
    );
};
